import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import { FileIcon } from '../../../assets';
import { Body2, Button } from '../../../components/common';
import { FileInput, FileInputValue, FormActions, InputField, TextAreaElement } from '../../../components/common/forms';
import { scrollToTop } from '../../../utils';
import { FormPageContainer } from '../FormPageContainer';
import { Column, ContentBlock, Form, FormRow, GridContainer, Row } from '../layoutComponents';
import { H2, H3, TextBlock } from '../Text';
import { GeneralExpenseReportPreview } from './GeneralExpenseReportPreview';
import { GeneralExpenseReportFormState, schema } from './schema';
import { ExpenseReportForm, ExpenseReportFormType } from '../../../types/expense-reports';
import { NotificationType, useAppDispatch } from '../../../context/appContext';
import { showNotification } from '../../../context/appActions';
import Submitted from '../Submitted';
import { createExpenseReport, submitExpenseReport, updateExpenseReport } from '../../../apis_kenno';
import FormError from '../../../components/common/forms/FormError';
import { getFileDisplayName, objectToFormData } from '../form-utils';
import { Divider } from '../../../components/common/Divider';

const ContactInfoContent = styled(ContentBlock)``;
const EventContent = styled(ContentBlock)``;
const PurchaseReportsContent = styled(ContentBlock)``;

const TotalAmount = styled.span`
  font-size: 24px;
  line-height: 36px;
  font-family: ${(props) => props.theme.text.secondaryFont};
`;

interface Props {
  updateForm?: ExpenseReportForm;
}

export const GeneralExpenseReport: React.FC<Props> = ({ updateForm }) => {
  const appDispatch = useAppDispatch();
  const [isBusy, setIsBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialId = updateForm?.id || null;
  const signedUrls = updateForm?.signedUrls || [];
  const [formId, setFormId] = useState<string | null>(initialId);
  const [submitted, setSubmitted] = useState(false);

  const initialValues = {
    purchaseReports: [{ report: '', amount: 0.0, receipts: undefined }],
  };

  const formMethods = useForm<GeneralExpenseReportFormState>({
    resolver: zodResolver(schema),
    defaultValues: updateForm ? { ...initialValues, ...updateForm.formData } : { ...initialValues },
  });

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);

  const t = useMessageGetter('forms.generalExpenseReport');
  const tCommon = useMessageGetter('forms');

  const handleSubmitFinal = async (data: GeneralExpenseReportFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    // Convert to FormData to support file uploads
    const formData = objectToFormData(data);
    formData.append('formType', ExpenseReportFormType.GeneralExpenseReport);

    try {
      let id = formId;
      if (id) {
        await updateExpenseReport(id, formData);
      } else {
        id = (await createExpenseReport(formData)).id;
      }
      await submitExpenseReport(id);
      handleEmptyForm();
      setSubmitted(true);
      scrollToTop();
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.submit'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSave = async (data: GeneralExpenseReportFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    // Convert to FormData to support file uploads
    const formData = objectToFormData(data);
    formData.append('formType', ExpenseReportFormType.GeneralExpenseReport);

    try {
      if (!formId) {
        const expenseReport = await createExpenseReport(formData);
        setFormId(expenseReport.id);
      } else {
        await updateExpenseReport(formId, formData);
      }
      await showNotification(appDispatch, tCommon('info.saved'), NotificationType.SUCCESS);
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.save'));
    } finally {
      setIsBusy(false);
    }
  };

  const {
    fields: purchaseReportFields,
    append,
    remove,
  } = useFieldArray({
    control: formMethods.control,
    name: 'purchaseReports',
  });

  const purchaseReports = formMethods.watch('purchaseReports');
  const maximumExpensesAdded = purchaseReportFields.length >= 3;

  const handleAddNewPurchaseReport = () => {
    if (maximumExpensesAdded) {
      return;
    }
    append({
      report: '',
      amount: 0.0,
      // This typecasting is a workaround for typescript error stating that receipt can't be undefined
      // This works differently than useForm's defaultValues in which the values can be null or undefined.
      // Unfortunately append function is strictly following the schema in which the values are required.
      // Discussion about the flaw here: https://github.com/orgs/react-hook-form/discussions/10211
      receipts: undefined as unknown as FileList,
    });
  };

  const handleEmptyForm = () => {
    formMethods.reset();
  };

  const handleTogglePreview = () => {
    setPreviewOpen(!previewOpen);
    scrollToTop();
  };
  const totalExpensesSum = purchaseReports.reduce((sum, obj) => sum + obj.amount, 0);
  const totalExpensesAmount = !isNaN(totalExpensesSum) ? totalExpensesSum.toFixed(2) : '';

  const translationPrefix = 'forms.generalExpenseReport';

  return (
    <FormPageContainer<GeneralExpenseReportFormState> formMethods={formMethods}>
      {!previewOpen && <H2>{t('title')}</H2>}
      {submitted ? (
        <Submitted message={tCommon('info.submitted')} />
      ) : (
        <Form>
          {previewOpen ? (
            <GeneralExpenseReportPreview formData={formMethods.getValues()} signedUrls={signedUrls} />
          ) : (
            <>
              <span>{t('description')}</span>
              <ContactInfoContent>
                <H3>{t('contactInfo.title')}</H3>
                <div>
                  <FormRow>
                    <InputField<GeneralExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.name"
                      type="text"
                      required
                    />
                  </FormRow>
                  <InputField<GeneralExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="contactInfo.address"
                    type="text"
                    required
                  />
                  <FormRow>
                    <InputField<GeneralExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.zip"
                      type="text"
                      required
                    />
                    <InputField<GeneralExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.city"
                      type="text"
                      required
                    />
                  </FormRow>
                  <FormRow>
                    <InputField<GeneralExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.phone"
                      type="text"
                      required
                    />
                    <InputField<GeneralExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.email"
                      type="text"
                      required
                    />
                  </FormRow>
                  <InputField<GeneralExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="contactInfo.iban"
                    type="text"
                    required
                  />
                </div>
              </ContactInfoContent>
              <EventContent>
                <H3>{t('event.title')}</H3>
                <div>
                  <InputField<GeneralExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="event.name"
                    type="text"
                    required
                  />
                  <InputField<GeneralExpenseReportFormState, string>
                    fieldPath="event.date"
                    translationPrefix={translationPrefix}
                    type="date"
                    width="50%"
                    required
                  />
                  <InputField<GeneralExpenseReportFormState, string>
                    fieldPath="event.additionalInformation"
                    translationPrefix={translationPrefix}
                    inputElementRenderer={(props) => <TextAreaElement {...props} />}
                  />
                </div>
              </EventContent>
              <PurchaseReportsContent>
                <H3>{t('purchaseReports.title')}</H3>
                <span>{t('purchaseReports.description')}</span>
                <div>
                  {purchaseReportFields.map((field, index) => {
                    const fileName = getFileDisplayName(purchaseReports[index]?.receipts?.[0]);
                    return (
                      <div key={field.id} style={{ marginBottom: '24px' }}>
                        <GridContainer columns={4} mdColumns={1} columnGap="20px">
                          <Column span={3} mdSpan={1}>
                            <InputField<GeneralExpenseReportFormState, string>
                              fieldPath={`purchaseReports.${index}.report`}
                              translationPrefix={translationPrefix}
                              type="text"
                              required
                            />
                          </Column>
                          <Column>
                            <InputField<GeneralExpenseReportFormState, string>
                              translationPrefix={translationPrefix}
                              fieldPath={`purchaseReports.${index}.amount`}
                              required
                              type="number"
                              step="0.01"
                            />
                          </Column>
                        </GridContainer>
                        <FormRow>
                          <InputField<GeneralExpenseReportFormState, FileInputValue>
                            translationPrefix={translationPrefix}
                            hideLabel
                            fieldPath={`purchaseReports.${index}.receipts`}
                            inputElementRenderer={(props) => (
                              <FormRow>
                                <FileInput onChange={props.onChange} label={t('purchaseReports.addReceipt')} />
                                {fileName && (
                                  <Row style={{ alignItems: 'center' }}>
                                    <FileIcon style={{ flexShrink: 0 }} />
                                    <TextBlock>{fileName}</TextBlock>
                                  </Row>
                                )}
                              </FormRow>
                            )}
                          />
                        </FormRow>
                        {purchaseReportFields.length > 1 && (
                          <>
                            <Button primary onClick={() => remove(index)}>
                              {t('purchaseReports.removePurchase')}
                            </Button>
                            <Divider style={{ margin: '24px 0' }} />
                          </>
                        )}
                      </div>
                    );
                  })}
                  <FormRow style={{ alignItems: 'center' }}>
                    <Button
                      type="button"
                      icon="plus"
                      onClick={handleAddNewPurchaseReport}
                      disabled={maximumExpensesAdded}
                    >
                      {t('purchaseReports.addNewPurchase')}
                    </Button>
                    {maximumExpensesAdded && <Body2>{t('purchaseReports.limitReached')}</Body2>}
                  </FormRow>
                </div>

                <TotalAmount>{t('purchaseReports.total', { amount: totalExpensesAmount })}</TotalAmount>
              </PurchaseReportsContent>
            </>
          )}
          {submitFailed && <FormError message={errorMessage} />}
          <FormActions
            handleEmptyForm={handleEmptyForm}
            handleTogglePreview={handleTogglePreview}
            handleSave={formMethods.handleSubmit(handleSave)}
            handleSubmitFinal={formMethods.handleSubmit(handleSubmitFinal)}
            actionButtonType="send"
            previewOpen={previewOpen}
            disabled={isBusy}
          />
        </Form>
      )}
    </FormPageContainer>
  );
};
